import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  KeyRound,
  Ban,
  RotateCw,
  Plus,
  ShieldCheck,
  Search,
  Loader2,
  Check,
  X,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ADMIN_EMAIL = "johan.gotvall@gmail.com";

const STATUS_META = {
  approved: { label: "Aktiv", color: "#34C759" },
  pending: { label: "Väntar", color: "#FFD60A" },
  rejected: { label: "Nekad", color: "#FF3B30" },
  disabled: { label: "Avstängd", color: "#71717A" },
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  // confirmAction shape: { userId, email, kind: "reset" | "disable" | "enable" | "add-license" }
  const [clientErrors, setClientErrors] = useState([]);
  const [errorPlatform, setErrorPlatform] = useState(""); // '' | iphone | android | windows | mac | linux
  const [loadingErrors, setLoadingErrors] = useState(false);

  const isAdmin = (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) {
      navigate("/", { replace: true });
    }
  }, [isAdmin, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Kunde inte hämta användare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const loadClientErrors = async (platform = errorPlatform) => {
    setLoadingErrors(true);
    try {
      const params = platform ? { platform } : {};
      const { data } = await api.get("/admin/client-errors", { params });
      setClientErrors(data);
    } catch {
      toast.error("Kunde inte hämta klientfel");
    } finally {
      setLoadingErrors(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadClientErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, errorPlatform]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.email.toLowerCase().includes(q));
  }, [users, search]);

  const runAction = async () => {
    if (!confirmAction) return;
    const { userId, kind } = confirmAction;
    setBusyId(userId);
    try {
      if (kind === "reset") {
        await api.post(`/admin/users/${userId}/reset-password`);
        toast.success("Lösenordet är återställt till 123456");
      } else if (kind === "disable") {
        await api.post(`/admin/users/${userId}/disable`);
        toast.success("Kontot är avstängt");
      } else if (kind === "enable") {
        await api.post(`/admin/users/${userId}/enable`);
        toast.success("Kontot är återaktiverat");
      } else if (kind === "add-license") {
        await api.post(`/admin/users/${userId}/add-license`);
        toast.success("Ytterligare MV-licens tillagd");
      } else if (kind === "approve-account") {
        await api.post(`/admin/users/${userId}/approve-account`);
        toast.success("Kontot är godkänt · startlösenord 123456");
      } else if (kind === "reject-account") {
        await api.post(`/admin/users/${userId}/reject-account`);
        toast.success("Ansökan nekad");
      } else if (kind === "reject-license") {
        await api.post(`/admin/users/${userId}/reject-license`);
        toast.success("Licensförfrågan nekad");
      } else if (kind === "delete") {
        await api.delete(`/admin/users/${userId}`);
        toast.success("Kontot är borttaget");
      }
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Åtgärden misslyckades";
      toast.error(msg);
    } finally {
      setBusyId(null);
      setConfirmAction(null);
    }
  };

  const confirmCopy = {
    reset: {
      title: "Återställ lösenord?",
      body: "Användarens lösenord sätts tillbaka till 123456. Meddela användaren manuellt.",
      confirmLabel: "Återställ",
      color: "#34C759",
    },
    disable: {
      title: "Stäng av konto?",
      body: "Inloggning blockeras. All sparad data behålls och kontot kan återaktiveras när som helst.",
      confirmLabel: "Stäng av",
      color: "#FF3B30",
    },
    enable: {
      title: "Återaktivera konto?",
      body: "Kontot får åter tillgång och kan logga in med sitt tidigare lösenord.",
      confirmLabel: "Återaktivera",
      color: "#34C759",
    },
    "add-license": {
      title: "Lägg till MV-licens?",
      body: "Användaren får skapa ytterligare en målvaktsprofil. Eventuell väntande licensförfrågan markeras som godkänd.",
      confirmLabel: "Lägg till",
      color: "#34C759",
    },
    "approve-account": {
      title: "Godkänn konto?",
      body: "Användaren aktiveras och kan logga in med startlösenord 123456. Ett bekräftelsemail försöker skickas (kan tystfela i sandbox-läge – meddela användaren själv om nödvändigt).",
      confirmLabel: "Godkänn",
      color: "#34C759",
    },
    "reject-account": {
      title: "Neka ansökan?",
      body: "Kontot markeras som nekat och kan inte logga in. Datan behålls.",
      confirmLabel: "Neka",
      color: "#FF3B30",
    },
    "reject-license": {
      title: "Neka licensförfrågan?",
      body: "Användarens väntande licensförfrågan markeras som nekad. Kontot behålls oförändrat.",
      confirmLabel: "Neka",
      color: "#FF3B30",
    },
    "delete": {
      title: "Ta bort konto?",
      body: "Kontot samt ALL data (målvakter, sessioner, skott, delnings-länkar) raderas permanent. Kan inte ångras.",
      confirmLabel: "Ta bort",
      color: "#FF3B30",
    },
  };

  const meta = confirmAction ? confirmCopy[confirmAction.kind] : null;

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Administration" backTo="/" backLabel="Tillbaka" />

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#34C759] font-display mb-2">
              <ShieldCheck size={14} /> Admin
            </div>
            <h1 className="font-display font-black uppercase tracking-widest text-4xl md:text-5xl text-white">
              Användare
            </h1>
            <div className="text-zinc-400 mt-2">
              Hantera användare, återställ lösenord, stäng av konton och tilldela extra MV-licenser.
            </div>
          </div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="admin-user-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök e-post…"
              className="pl-9 h-11 w-72 rounded-none border-[#27272A] bg-[#0A0A0A] text-white placeholder:text-zinc-600"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="card-tactical p-16 text-center"
            data-testid="admin-empty"
          >
            <div className="font-display font-black text-xl uppercase tracking-widest mb-3">
              Inga användare
            </div>
            <div className="text-zinc-400">
              {search ? "Ingen träff. Prova en annan sökning." : "Inga konton finns ännu."}
            </div>
          </div>
        ) : (
          <div className="card-tactical overflow-hidden" data-testid="admin-user-table">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#27272A] bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
              <div className="col-span-5">E-post</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-center">Licens</div>
              <div className="col-span-4 text-right">Åtgärder</div>
            </div>
            {filtered.map((u) => {
              const meta = STATUS_META[u.status] || STATUS_META.pending;
              const isDisabled = u.status === "disabled";
              const busy = busyId === u.id;
              return (
                <div
                  key={u.id}
                  className="grid grid-cols-12 gap-3 items-center px-5 py-4 border-b border-[#171717] last:border-b-0 hover:bg-[#111]"
                  data-testid={`admin-user-row-${u.email}`}
                >
                  <div className="col-span-5 min-w-0">
                    <div className="text-white font-display tracking-wide truncate flex items-center gap-2">
                      {u.email}
                      {u.is_admin && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#34C759] text-[#34C759]">
                          Admin
                        </span>
                      )}
                      {u.has_pending_license_request && (
                        <span
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FFD60A] text-[#FFD60A]"
                          title="Väntande licensförfrågan"
                        >
                          Licens?
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-600 mt-1">
                      {u.activated_at
                        ? `Senast aktiv ${new Date(u.activated_at).toLocaleDateString("sv-SE")}`
                        : "Aldrig inloggad"}
                    </div>
                  </div>

                  <div className="col-span-2">
                    <span
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-display"
                      style={{ color: meta.color }}
                    >
                      <span
                        className="inline-block w-2 h-2"
                        style={{ background: meta.color }}
                      />
                      {meta.label}
                    </span>
                  </div>

                  <div className="col-span-1 text-center text-white font-display">
                    {u.used_licenses}/{u.license_count}
                  </div>

                  <div className="col-span-4 flex items-center gap-2 justify-end flex-wrap">
                    {u.status === "pending" ? (
                      <>
                        <Button
                          data-testid={`admin-approve-${u.email}`}
                          disabled={busy}
                          onClick={() =>
                            setConfirmAction({ userId: u.id, email: u.email, kind: "approve-account" })
                          }
                          className="h-9 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black text-[10px] uppercase tracking-widest font-display"
                          title="Godkänn konto"
                        >
                          <Check size={13} className="mr-1.5" />
                          Godkänn
                        </Button>
                        <Button
                          data-testid={`admin-reject-${u.email}`}
                          disabled={busy}
                          onClick={() =>
                            setConfirmAction({ userId: u.id, email: u.email, kind: "reject-account" })
                          }
                          variant="outline"
                          className="h-9 rounded-none border-[#FF3B30]/60 bg-transparent hover:bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] uppercase tracking-widest font-display"
                          title="Neka ansökan"
                        >
                          <X size={13} className="mr-1.5" />
                          Neka
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          data-testid={`admin-reset-${u.email}`}
                          disabled={busy || u.is_admin}
                          onClick={() =>
                            setConfirmAction({ userId: u.id, email: u.email, kind: "reset" })
                          }
                          variant="outline"
                          className="h-9 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white text-[10px] uppercase tracking-widest font-display disabled:opacity-40"
                          title="Återställ lösenord"
                        >
                          <KeyRound size={13} className="mr-1.5" />
                          Reset
                        </Button>

                        {isDisabled ? (
                          <Button
                            data-testid={`admin-enable-${u.email}`}
                            disabled={busy}
                            onClick={() =>
                              setConfirmAction({ userId: u.id, email: u.email, kind: "enable" })
                            }
                            className="h-9 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black text-[10px] uppercase tracking-widest font-display"
                          >
                            <RotateCw size={13} className="mr-1.5" />
                            Återaktivera
                          </Button>
                        ) : (
                          <Button
                            data-testid={`admin-disable-${u.email}`}
                            disabled={busy || u.is_admin || u.status !== "approved"}
                            onClick={() =>
                              setConfirmAction({ userId: u.id, email: u.email, kind: "disable" })
                            }
                            variant="outline"
                            className="h-9 rounded-none border-[#FF3B30]/60 bg-transparent hover:bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] uppercase tracking-widest font-display disabled:opacity-40"
                            title="Stäng av"
                          >
                            <Ban size={13} className="mr-1.5" />
                            Stäng av
                          </Button>
                        )}

                        <Button
                          data-testid={`admin-add-license-${u.email}`}
                          disabled={busy || u.status !== "approved"}
                          onClick={() =>
                            setConfirmAction({ userId: u.id, email: u.email, kind: "add-license" })
                          }
                          variant="outline"
                          className="h-9 rounded-none border-[#34C759]/60 bg-transparent hover:bg-[#34C759]/10 text-[#34C759] text-[10px] uppercase tracking-widest font-display disabled:opacity-40"
                          title="Lägg till MV-licens"
                        >
                          {busy ? (
                            <Loader2 size={13} className="mr-1.5 animate-spin" />
                          ) : (
                            <Plus size={13} className="mr-1.5" />
                          )}
                          MV-licens
                        </Button>

                        {u.has_pending_license_request && (
                          <Button
                            data-testid={`admin-reject-license-${u.email}`}
                            disabled={busy}
                            onClick={() =>
                              setConfirmAction({ userId: u.id, email: u.email, kind: "reject-license" })
                            }
                            variant="outline"
                            className="h-9 rounded-none border-[#FF3B30]/60 bg-transparent hover:bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] uppercase tracking-widest font-display"
                            title="Neka licensförfrågan"
                          >
                            <X size={13} className="mr-1.5" />
                            Neka lic.
                          </Button>
                        )}
                      </>
                    )}
                    {!u.is_admin && (
                      <Button
                        data-testid={`admin-delete-${u.email}`}
                        disabled={busy}
                        onClick={() =>
                          setConfirmAction({ userId: u.id, email: u.email, kind: "delete" })
                        }
                        variant="outline"
                        className="h-9 w-9 p-0 rounded-none border-[#FF3B30]/60 bg-transparent hover:bg-[#FF3B30]/20 text-[#FF3B30]"
                        title="Ta bort konto"
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <section className="card-tactical p-5 mt-10" data-testid="client-errors-section">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
            <div className="section-hdr">
              <span className="marker" />
              Klientfel · senaste krascher
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {["", "iphone", "android", "windows", "mac", "linux"].map((p) => (
                <button
                  key={p || "all"}
                  type="button"
                  data-testid={`client-errors-filter-${p || "all"}`}
                  data-active={errorPlatform === p}
                  onClick={() => setErrorPlatform(p)}
                  className={`h-8 px-3 text-[11px] font-display uppercase tracking-widest border ${
                    errorPlatform === p
                      ? "border-[#34C759] text-white bg-[#0f1a12]"
                      : "border-[#27272A] text-zinc-400 hover:text-white hover:border-[#3F3F46]"
                  }`}
                >
                  {p === "" ? "Alla" : p === "iphone" ? "iPhone" : p === "mac" ? "macOS" : p[0].toUpperCase() + p.slice(1)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => loadClientErrors()}
                className="h-8 px-3 text-[11px] font-display uppercase tracking-widest border border-[#27272A] text-zinc-400 hover:text-white hover:border-[#3F3F46]"
                data-testid="client-errors-refresh"
              >
                Uppdatera
              </button>
            </div>
          </div>
          {loadingErrors ? (
            <div className="text-zinc-500 text-sm">Laddar…</div>
          ) : clientErrors.length === 0 ? (
            <div className="text-zinc-500 text-sm" data-testid="client-errors-empty">
              Inga krascher att visa.
            </div>
          ) : (
            <div className="space-y-2">
              {clientErrors.map((e, i) => (
                <details
                  key={`${e.created_at}-${i}`}
                  className="border border-[#27272A] p-3"
                  data-testid={`client-error-row-${i}`}
                >
                  <summary className="cursor-pointer flex items-center justify-between gap-2 flex-wrap list-none">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[9px] tracking-widest font-bold border px-1.5 py-0.5 ${
                          e.platform === "iphone"
                            ? "border-[#FF3B30] text-[#FF3B30]"
                            : e.platform === "android"
                            ? "border-[#34C759] text-[#34C759]"
                            : e.platform === "windows"
                            ? "border-[#0A84FF] text-[#0A84FF]"
                            : "border-zinc-500 text-zinc-500"
                        }`}
                      >
                        {(e.platform || "other").toUpperCase()}
                      </span>
                      <span className="text-xs text-white truncate max-w-[480px]">
                        {e.message || "(okänt fel)"}
                      </span>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono-num">
                      {e.created_at ? new Date(e.created_at).toLocaleString("sv-SE") : ""}
                    </span>
                  </summary>
                  <div className="mt-3 space-y-2 text-[11px]">
                    <div>
                      <span className="text-zinc-500 uppercase tracking-widest font-display">URL: </span>
                      <span className="text-zinc-300 break-all">{e.url || "–"}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 uppercase tracking-widest font-display">UA: </span>
                      <span className="text-zinc-400 break-all">{e.user_agent || "–"}</span>
                    </div>
                    {e.stack && (
                      <div>
                        <div className="text-zinc-500 uppercase tracking-widest font-display mb-1">Stack</div>
                        <pre className="bg-black border border-[#27272A] p-2 text-[10px] text-zinc-400 overflow-auto whitespace-pre-wrap break-words max-h-40">
{e.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>
      </main>

      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(o) => !o && setConfirmAction(null)}
      >
        <AlertDialogContent className="bg-[#111] border-[#27272A] text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest">
              {meta?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmAction?.email && (
                <span className="block mb-2 text-white font-display tracking-wide">
                  {confirmAction.email}
                </span>
              )}
              {meta?.body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white">
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-confirm-action"
              onClick={runAction}
              className="rounded-none font-display uppercase tracking-widest text-black"
              style={{ backgroundColor: meta?.color || "#34C759" }}
            >
              {meta?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
